import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { render, scene } from '@/threejs/core';
import * as THREE from 'three';

/**
 * MaterialManager - Centralized management for scene materials
 * Handles material overrides and updates
 */
class MaterialManager {
  constructor() {
    this.globalMaterialOverride = false;
    this.materialOverrideColor = '#ffffff';
    this.isInitialized = false;
  }

  /**
   * Initialize the material manager
   */
  initialize() {
    this.isInitialized = true;
    return this;
  }

  /**
   * Check if manager is initialized
   * @returns {boolean}
   */
  isManagerInitialized() {
    return this.isInitialized;
  }

  /**
   * Set ambient light enabled state
   * @param {boolean} enabled - Whether ambient light is enabled
   */
  setMaterialOverride(enabled) {
    this.globalMaterialOverride = enabled;
    this.updateMaterial();
  }

  setMaterialOverrideColor(color) {
    const c = color.startsWith('#') ? color : `#${color}`;
    this.materialOverrideColor = c;
    this.updateMaterial();
  }

  /**
   * Override materials in scene
   */
  updateMaterial() {
    scene.traverse((child) => {
      if (child.isLight || child.isCamera) return;
      if (child.userData.excludeFromOutline) return;
      if (!child.userData.edgeOutline) return;

      if (this.globalMaterialOverride) {
        if (child.material) {
          if (!child.userData.originalMaterial) {
            child.userData.originalMaterial = child.material;
          }

          const luminance = child.userData.luminance || 0;
          child.material = new THREE.MeshStandardMaterial({
            color: this.materialOverrideColor,
            ...(luminance > 0 && {
              emissive: new THREE.Color(this.materialOverrideColor),
              emissiveIntensity: luminance / 100,
            }),
          });
          child.needsUpdate = true;
        }
      } else {
        if (child.userData.originalMaterial) {
          const overrideMaterial = child.material;
          child.material = child.userData.originalMaterial;
          delete child.userData.originalMaterial;
          if (overrideMaterial) overrideMaterial.dispose();
          child.needsUpdate = true;
        }
      }
    });
    render();
  }

  /**
   * Update the Crux module store with lighting settings
   * @param {object} settings - Lighting settings to update in store
   */
  updateStoreSettings(settings) {
    try {
      const cruxModuleStore = useCruxModuleStore();

      cruxModuleStore.mergeSettingModule(
        null,
        'viewport_settings',
        'render.globalMaterialOverride',
        settings.globalMaterialOverride
      );
    } catch (error) {
      console.warn('Could not update store settings:', error);
    }
  }

  /**
   * Handle lighting setting changes
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   */
  handleMaterialSetting(key, value) {
    switch (key) {
      case 'render.globalMaterialOverride':
        this.setMaterialOverride(value);
        break;

      case 'render.materialOverrideColor':
        this.setMaterialOverrideColor(value);
        break;
      default:
        console.warn(`Unknown material setting: ${key}`);
    }
  }

  /**
   * Get current material configuration
   * @returns {object} Current material settings
   */
  getMaterialConfig() {
    return {
      globalMaterialOverride: this.globalMaterialOverride,
      materialOverrideColor: this.materialOverrideColor,
    };
  }

  /**
   * Dispose of lighting resources
   */
  dispose() {
    this.isInitialized = false;
  }
}

// Create and export singleton instance
export const materialManager = new MaterialManager();

// Export the class for testing or multiple instances if needed
export { MaterialManager };
